import React, { useEffect, useState } from 'react';
import CreateJKCTaskApi from '@/fetch/business/jkc/createTask';
import './index.scss';
import SelectStop from '../SelectStop';
import ContactListPopup from '../ContactListPopup';
import { showModal } from '@/components';
import { showErrorToast, showSuccessToast } from '@/utils/toast';
import { useNavigate } from 'react-router-dom';
import { JKCContact } from '@/jkc/utils/type';
import { JkcHttpStatusCode } from '@/jkc/utils/constant';
import { useTaskInfoStore } from '@/jkc/store';
import { debounce } from '@/utils';
import { HttpStatusCode } from '@/fetch/core/constant';

interface CheckStartContact {
  contactUserName: string;
  contactUserErp: string;
  contactUserPhone: string;
  contactUserId: string | number;
  contactUserBelongCode: string | number;
}

const CreateJKCTask = ({
  vehicleInfo,
  siteLocation,
  cityName
}: {
  vehicleInfo: any;
  siteLocation: any;
  cityName: string | null;
}) => {
  const navigate = useNavigate();

  const {
    taskInfo,
    updateStopPointAndPersonPoint,
    addContact,
    deleteContact,
    deleteContactsByUserIds,
    setProductType,
    resetTaskInfo
  } = useTaskInfoStore();
  const [contactPopupVisible, setContactPopupVisible] = useState(false);
  const [editingStopType, setEditingStopType] = useState<
    'load' | 'unload' | null
  >(null);
  const [userLocation, setUserLocation] = useState<null | number[]>(null);
  const [isSelectingContact, setIsSelectingContact] = useState(false);

  useEffect(() => {
    if (
      vehicleInfo &&
      vehicleInfo.vehicleInfoList.length > 0 &&
      !taskInfo.productType
    ) {
      setProductType(
        vehicleInfo.vehicleInfoList[0]?.serviceProvider || undefined
      );
    }
  }, [vehicleInfo]);

  useEffect(() => {
    if (
      taskInfo.productType &&
      vehicleInfo?.storeId &&
      userLocation &&
      (!taskInfo.load.personPoint.id || !taskInfo.load.stopPoint.id)
    ) {
      getRecommendStartPoint();
    }
  }, [taskInfo.productType, vehicleInfo?.storeId, userLocation]);

  useEffect(() => {
    const winAny = window as any;
    winAny.setLngLat = function (lng: number, lat: number) {
      if (lng && lat) {
        setUserLocation([lng, lat]);
      }
    };
    if (winAny.app && winAny.app.postMessage) {
      winAny.app.postMessage(
        JSON.stringify({ function: 'getLngLat', callBack: 'setLngLat' })
      );
    }
    return () => {
      winAny.setLngLat = null;
    };
  }, []);
  const getRecommendStartPoint = async () => {
    if (!vehicleInfo?.storeId || !taskInfo.productType || !userLocation) return;
    const res = await CreateJKCTaskApi.recommendStartPoint({
      storeId: vehicleInfo.storeId,
      serviceProvider: taskInfo.productType as any,
      location: { latitude: userLocation[1], longitude: userLocation[0] }
    });
    if (
      res.code === JkcHttpStatusCode.Success &&
      res.data &&
      res.data.poiList.length > 0
    ) {
      const startPoint = res.data.poiList[0];
      updateStopPointAndPersonPoint('load', {
        personPoint: {
          id: startPoint.id,
          title: startPoint.title,
          minivanPointTitle: startPoint.minivanPointTitle,
          address: startPoint.address,
          location: startPoint.location,
          source: startPoint.source,
          cityCode: startPoint.cityCode
        },
        stopPoint: {
          id: startPoint.id,
          title: startPoint.title,
          minivanPointTitle: startPoint.minivanPointTitle,
          address: startPoint.address,
          location: startPoint.location,
          source: startPoint.source,
          cityCode: startPoint.cityCode
        }
      });
    }
  };

  if (!vehicleInfo) {
    return <div>加载车辆信息中...</div>;
  }
  const productTypes = Array.from(
    new Set(
      vehicleInfo.vehicleInfoList.map((item: any) => ({
        serviceProductName: item.serviceProductName,
        serviceProvider: item.serviceProvider
      }))
    )
  );
  const filteredVehicles = vehicleInfo.vehicleInfoList.filter((item: any) =>
    taskInfo.productType ? item.serviceProvider === taskInfo.productType : true
  );

  const handleSelectLoadStop = () => {
    if (!vehicleInfo?.storeId) {
      showErrorToast('请先获取门店信息');
      return;
    }
    navigate('/jkc/selectPosition', {
      state: {
        storeId: vehicleInfo.storeId,
        stopType: 'load',
        serviceProvider: taskInfo.productType,
        cityName
      }
    });
  };

  const handleSelectUnloadStop = () => {
    if (!vehicleInfo?.storeId) {
      showErrorToast('请先获取门店信息');
      return;
    }
    navigate('/jkc/selectPosition', {
      state: {
        storeId: vehicleInfo.storeId,
        stopType: 'unload',
        serviceProvider: taskInfo.productType,
        cityName
      }
    });
  };

  const handleAddContact = (type: 'load' | 'unload') => {
    setEditingStopType(type);
    setContactPopupVisible(true);
  };

  const handleContactSelect = async (contact: JKCContact) => {
    if (!contact) {
      showErrorToast('请选择联系人');
      return;
    }

    if (!editingStopType || isSelectingContact) {
      return;
    }

    if (!contact.contactUserId) {
      showErrorToast('添加失败 联系人信息异常，请重新选择');
      return;
    }

    if (!contact.thirdPartnerBelongCode) {
      showErrorToast('添加失败 联系人站点信息异常');
      return;
    }

    setIsSelectingContact(true);

    try {
      const checkResponse = await CreateJKCTaskApi.checkContact({
        contactUserId: contact.contactUserId
      });

      if (checkResponse.code !== HttpStatusCode.Success) {
        showErrorToast('添加失败 该用户站点未开通叫车拉货功能');
        return;
      }

      const currentStopContacts = taskInfo[editingStopType].contactList;
      const hasInvalidContact = currentStopContacts.some(
        (item) => !item.thirdPartnerBelongCode
      );
      if (hasInvalidContact) {
        showErrorToast('添加失败 联系人站点信息异常');
        return;
      }

      const hasBelongCodeMismatch = currentStopContacts.some(
        (item) =>
          String(item.thirdPartnerBelongCode) !==
          String(contact.thirdPartnerBelongCode)
      );
      if (hasBelongCodeMismatch) {
        showErrorToast('添加失败 同一个点位不允许添加不同站点的联系人');
        return;
      }

      addContact(editingStopType, {
        userName: contact.userName,
        userPhone: contact.userPhone,
        contactUserId: contact.contactUserId,
        thirdPartnerBelongCode: contact.thirdPartnerBelongCode,
        contactUserErp: contact.contactUserErp
      });
      setContactPopupVisible(false);
      setEditingStopType(null);
    } catch (error) {
      showErrorToast('添加失败，请稍后重试');
    } finally {
      setIsSelectingContact(false);
    }
  };

  const handleDeleteContact = (
    type: 'load' | 'unload',
    contact: JKCContact
  ) => {
    deleteContact(type, contact);
  };

  const submitOrder = async () => {
    const res = await CreateJKCTaskApi.createOrder({
      storeId: vehicleInfo?.storeId,
      startPoi: taskInfo.load.personPoint,
      pickUpPoint: {
        poi: taskInfo.load.stopPoint,
        orderContractUsers: taskInfo.load.contactList
      },
      endPoi: taskInfo.unload.personPoint,
      dropOffPoint: {
        poi: taskInfo.unload.stopPoint,
        orderContractUsers: taskInfo.unload.contactList
      },
      siteLocation: siteLocation,
      serviceInfoDTO: {
        ...vehicleInfo.serviceInfo,
        serviceProviderProducts: vehicleInfo.vehicleInfoList.filter(
          (v: any) => v.serviceProvider === taskInfo.productType
        )
      }
    });
    if (res.code === JkcHttpStatusCode.Success) {
      resetTaskInfo();
      showSuccessToast('订单创建成功');
      navigate(
        `/jkc/orderDetail?orderId=${res.data.orderId}&storeId=${vehicleInfo?.storeId}`
      );
    } else {
      showErrorToast(res.msg);
    }
  };

  const createOrder = async () => {
    if (!taskInfo.load.contactList || taskInfo.load.contactList.length <= 0) {
      showErrorToast('请选择装货联系人');
      return;
    }
    if (!taskInfo.load.stopPoint.title) {
      showErrorToast('请选择装货点');
      return;
    }
    if (
      !taskInfo.unload.contactList ||
      taskInfo.unload.contactList.length <= 0
    ) {
      showErrorToast('请选择卸货联系人');
      return;
    }
    if (!taskInfo.unload.stopPoint.title) {
      showErrorToast('请选择卸货点');
      return;
    }

    const contactUserIdMap = new Map<string, string | number>();
    [...taskInfo.load.contactList, ...taskInfo.unload.contactList]
      .map((contact) => contact.contactUserId)
      .filter(
        (
          contactUserId
        ): contactUserId is string | number =>
          typeof contactUserId !== 'undefined' && contactUserId !== null
      )
      .forEach((contactUserId) => {
        contactUserIdMap.set(String(contactUserId), contactUserId);
      });

    const contactUserIdList = Array.from(contactUserIdMap.values());

    if (contactUserIdList.length <= 0) {
      await submitOrder();
      return;
    }

    try {
      const checkStartResponse = await CreateJKCTaskApi.checkStart({
        contactUserIdList
      });

      if (checkStartResponse.code !== HttpStatusCode.Success) {
        showErrorToast(checkStartResponse.message || '校验失败，请稍后重试');
        return;
      }

      const invalidContacts: CheckStartContact[] = Array.isArray(
        checkStartResponse.data
      )
        ? checkStartResponse.data
        : [];

      if (invalidContacts.length > 0) {
        const invalidContactNames = invalidContacts
          .map((contact) => contact.contactUserName)
          .join('、');
        const invalidContactUserIds = invalidContacts
          .map((contact) => contact.contactUserId)
          .filter(
            (
              contactUserId
            ): contactUserId is string | number =>
              typeof contactUserId !== 'undefined' && contactUserId !== null
          );

        showModal({
          content: `${invalidContactNames}所属站点未开通叫车拉货功能，请更换其他联系人。`,
          footer: [
            {
              type: 'primary',
              btnText: '我知道了',
              autoClose: true,
              onClick: () => {
                deleteContactsByUserIds(invalidContactUserIds);
              }
            }
          ]
        });
        return;
      }

      await submitOrder();
    } catch (error) {
      showErrorToast('校验失败，请稍后重试');
    }
  };

  return (
    <div className="create-jkc-task">
      {productTypes.length > 0 && (
        <div className="product-type-selector-wrapper">
          <div className="product-type-selector">
            {productTypes.map((type: any) => (
              <div
                key={type.serviceProvider}
                className={`product-type-btn ${
                  taskInfo.productType === type.serviceProvider ? 'active' : ''
                }`}
                onClick={(e) => {
                  resetTaskInfo();
                  setProductType(type.serviceProvider);
                  e.currentTarget.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest'
                  });
                }}
              >
                {type.serviceProductName}
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredVehicles.length > 0 && (
        <img src={filteredVehicles[0].iconUrl} className="vehicle-icon" />
      )}

      <div className="create-task-container">
        <SelectStop
          stopName={taskInfo.load.stopPoint.title}
          contactList={taskInfo.load.contactList}
          placeholder="请选择装货点"
          type="load"
          onSelectStop={handleSelectLoadStop}
          onAddContact={() => handleAddContact('load')}
          onDeleteContact={(contact) => handleDeleteContact('load', contact)}
        />
        <SelectStop
          stopName={taskInfo.unload.stopPoint.title}
          contactList={taskInfo.unload.contactList}
          placeholder="请选择卸货点"
          type="unload"
          onSelectStop={handleSelectUnloadStop}
          onAddContact={() => handleAddContact('unload')}
          onDeleteContact={(contact) => handleDeleteContact('unload', contact)}
        />
      </div>

      <ContactListPopup
        visible={contactPopupVisible}
        onClose={() => setContactPopupVisible(false)}
        onSelect={handleContactSelect}
      />
      <div
        className="call-car-btn"
        clstag="transport_workbench|callCarImmediately"
        onClick={debounce(createOrder, 300, true)}
      >
        <div className="left">立享0元</div>
        <div className="right">{'立即叫车 >'}</div>
      </div>
    </div>
  );
};

export default CreateJKCTask;
